import { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Users2, Crown, Car, RefreshCw, ChevronRight, ChevronDown,
  Calendar, CheckCircle2, Clock3, Building2, X, MapPin,
} from "lucide-react";
import { API } from "../../config";
import { CountryFlag } from "../shared/CountryFlag";
import { serviceTypeLabel } from "../../utils/serviceTypeLabel";
import { useCountryFilter } from "../../contexts/CountryFilterContext";
import { CustomerAddendaTimelineWidget } from "./CustomerAddendaTimelineWidget";

// Custom-event channel: other widgets emit `c360:filter-country` with
// `{ country: "DE" }` and the Customer 360 widget picks it up, applies
// the filter and scrolls itself into view. Zero coupling between
// widgets, no shared context provider needed.
export const C360_FILTER_COUNTRY_EVENT = "c360:filter-country";


const LOYALTY_STYLES = {
  top_loyal: { label: "Top Fedele", icon: Crown,  color: "text-lojack-clause-400", bg: "bg-lojack-clause-500/10", border: "border-lojack-clause-500/30" },
  recurring: { label: "Ricorrente", icon: Users2, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" },
  new:       { label: "Nuovo",      icon: Car,    color: "text-gray-400", bg: "bg-gray-500/10", border: "border-gray-500/30" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  try {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days < 1) return "oggi";
    if (days < 7) return `${days}g fa`;
    if (days < 30) return `${Math.floor(days / 7)} sett. fa`;
    if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
    return `${Math.floor(days / 365)} anni fa`;
  } catch { return "—"; }
};


// ─── Timeline (vehicle-level) ─────────────────────────────────────────

const VehicleTimeline = ({ vehicle, index }) => {
  // Build chronological events for this vehicle, skipping null timestamps.
  const events = [
    { key: "created_at",                  label: "Cliente creato dal Dealer",   ts: vehicle.created_at,                  done: !!vehicle.created_at },
    { key: "personal_data_completed_at",  label: "Dati personali compilati",    ts: vehicle.personal_data_completed_at,  done: !!vehicle.personal_data_completed_at },
    { key: "gdpr_preferences_at",         label: "Preferenze GDPR salvate",     ts: vehicle.gdpr_preferences_at,         done: !!vehicle.gdpr_preferences_at },
    { key: "preview_first_opened_at",     label: "Anteprima contratto vista",   ts: vehicle.preview_first_opened_at,     done: !!vehicle.preview_first_opened_at },
    { key: "contract_signed_at",          label: "Contratto firmato",            ts: vehicle.contract_signed_at,          done: !!vehicle.contract_signed && !!vehicle.contract_signed_at },
  ];

  const vehicleLabel = [vehicle.brand, vehicle.model].filter(Boolean).join(" ") || "Veicolo";
  const plate = vehicle.plate || "—";
  const vin = vehicle.vin || "—";

  return (
    <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4" data-testid={`c360-vehicle-${index}`}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-lojack-red/10 flex items-center justify-center shrink-0">
            <Car size={14} className="text-lojack-red" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{vehicleLabel}</p>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">
              <span className="font-mono">{plate}</span> · <span className="font-mono opacity-70">{vin}</span>
            </p>
          </div>
        </div>
        {vehicle.contract_signed ? (
          <span className="px-2 py-0.5 rounded-full bg-lojack-blue-500/15 text-lojack-blue-400 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1">
            <CheckCircle2 size={10} /> Firmato
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-lojack-clause-500/15 text-lojack-clause-400 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1">
            <Clock3 size={10} /> In onboarding
          </span>
        )}
      </div>

      {/* Tiny dealer + service line */}
      <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-3 flex-wrap">
        {vehicle.dealer_name && (
          <span className="flex items-center gap-1"><Building2 size={10} /> {vehicle.dealer_name}</span>
        )}
        {vehicle.service_type && <span className="text-gray-400" data-testid="c360-service-type">{serviceTypeLabel(vehicle.service_type)}</span>}
      </div>

      {/* Timeline events */}
      <div className="space-y-1.5">
        {events.map((ev) => (
          <div key={ev.key} className="flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full shrink-0 ${ev.done ? "bg-lojack-blue-500" : "bg-gray-700"}`} />
            <span className={`flex-1 ${ev.done ? "text-gray-300" : "text-gray-600 line-through decoration-gray-800"}`}>
              {ev.label}
            </span>
            <span className="text-gray-500 text-[10px] whitespace-nowrap">
              {ev.done ? fmtDate(ev.ts) : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ─── Expandable customer row ──────────────────────────────────────────

const CustomerRow = ({ customer, index, expanded, onToggle }) => {
  const style = LOYALTY_STYLES[customer.loyalty] || LOYALTY_STYLES.new;
  const Icon = style.icon;

  return (
    <>
      <tr
        onClick={onToggle}
        className="border-b border-gray-800/50 hover:bg-purple-500/5 cursor-pointer transition-colors"
        data-testid={`c360-customer-row-${index}`}
      >
        <td className="py-3 px-2">
          {expanded
            ? <ChevronDown size={14} className="text-purple-400" />
            : <ChevronRight size={14} className="text-gray-500" />}
        </td>
        <td className="py-3 px-2">
          <p className="text-white text-sm font-medium truncate max-w-[220px]" title={customer.display_name}>
            {customer.display_name}
          </p>
          <p className="text-gray-500 text-[11px] truncate max-w-[220px]" title={customer.email}>
            {customer.email}
          </p>
        </td>
        <td className="py-3 px-2 text-center">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${style.bg} ${style.color} border ${style.border}`}>
            <Icon size={10} /> {style.label}
          </span>
        </td>
        <td className="py-3 px-2 text-center">
          <span className="text-white font-bold">{customer.total_vehicles}</span>
        </td>
        <td className="py-3 px-2 text-center">
          <span className="text-lojack-blue-400 font-semibold">{customer.signed_contracts}</span>
          <span className="text-gray-600">/{customer.total_vehicles}</span>
        </td>
        <td className="py-3 px-2 text-center text-gray-400 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <CountryFlag code={customer.country} />
            <span>{customer.country || "—"}</span>
          </span>
        </td>
        <td className="py-3 px-2 text-right text-gray-400 text-xs whitespace-nowrap">
          {fmtRelative(customer.last_activity)}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-gray-800/50 bg-[#0a0a0a]">
          <td colSpan={7} className="p-4">
            <div className="space-y-4" data-testid={`c360-customer-detail-${index}`}>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-black/40 border border-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Cliente dal</p>
                  <p className="text-white text-sm font-semibold flex items-center gap-1.5">
                    <Calendar size={12} className="text-purple-400" />
                    {fmtDate(customer.customer_since)}
                  </p>
                </div>
                <div className="bg-black/40 border border-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Prima firma</p>
                  <p className="text-white text-sm font-semibold">{fmtDate(customer.first_signed_at)}</p>
                </div>
                <div className="bg-black/40 border border-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Conversion</p>
                  <p className="text-white text-sm font-semibold">{customer.signing_rate_pct}%</p>
                </div>
                <div className="bg-black/40 border border-gray-800 rounded-lg p-2.5">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Lifetime</p>
                  <p className="text-white text-sm font-semibold">{customer.lifetime_days} giorni</p>
                </div>
              </div>

              {/* Vehicle timelines */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {customer.vehicles.map((v, i) => (
                  <VehicleTimeline key={v.customer_id || i} vehicle={v} index={i} />
                ))}
              </div>

              {/* GDPR Art. 16 rectification addenda — one section per signed vehicle */}
              {customer.vehicles.filter(v => v.contract_signed && v.customer_id).map((v, i) => (
                <div key={`addenda-${v.customer_id}`} className="space-y-2" data-testid={`c360-addenda-section-${i}`}>
                  <h4 className="text-gray-500 text-[10px] uppercase tracking-wider flex items-center gap-2">
                    <span className="inline-flex items-center gap-1">
                      <Car size={10} className="text-lojack-red" />
                      {[v.brand, v.model].filter(Boolean).join(" ") || "Veicolo"}
                    </span>
                    <span className="opacity-50">·</span>
                    <span className="font-mono opacity-70">{v.plate || v.vin || "—"}</span>
                  </h4>
                  <CustomerAddendaTimelineWidget customerId={v.customer_id} />
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};


// ─── Main widget ──────────────────────────────────────────────────────

export const Customer360Widget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minVehicles, setMinVehicles] = useState(1);
  const [sort, setSort] = useState("total_vehicles");
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [countryFilter, setCountryFilter] = useState(null);
  const containerRef = useRef(null);
  const { country: globalCountry, isFiltered } = useCountryFilter();

  // Keep the local ``countryFilter`` in sync with the global Admin
  // Dashboard filter. The local filter still accepts drill-down
  // overrides via the ``c360:filter-country`` custom event (e.g. from
  // WalletAdoptionWidget): clicking a country there sets the local
  // filter independently. Whenever the global filter changes again,
  // we re-sync so the operator stays oriented.
  useEffect(() => {
    setCountryFilter(isFiltered ? globalCountry : null);
  }, [globalCountry, isFiltered]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ min_vehicles: minVehicles, sort });
      if (countryFilter) params.set("country", countryFilter);
      const r = await axios.get(`${API}/dashboard/customer-360?${params}`);
      setData(r.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("customer-360 fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minVehicles, sort, countryFilter]);

  // Cross-widget listener: when WalletAdoptionWidget (or any other widget)
  // dispatches `c360:filter-country`, apply the filter and scroll us into
  // view + briefly pulse the border for a visual confirmation.
  useEffect(() => {
    const handler = (e) => {
      const code = e?.detail?.country;
      if (!code) return;
      setCountryFilter(code.toUpperCase());
      setMinVehicles(1);  // widen the result set when drilling-down
      setExpandedEmail(null);
      // Scroll & pulse
      requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (containerRef.current) {
          containerRef.current.classList.add("ring-2", "ring-purple-500/60");
          setTimeout(() => {
            containerRef.current?.classList.remove("ring-2", "ring-purple-500/60");
          }, 1500);
        }
      });
    };
    window.addEventListener(C360_FILTER_COUNTRY_EVENT, handler);
    return () => window.removeEventListener(C360_FILTER_COUNTRY_EVENT, handler);
  }, []);

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="customer-360-empty">
        {loading
          ? <div className="text-gray-500 text-sm flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Caricamento…</div>
          : <p className="text-gray-500 text-sm">Nessun dato disponibile</p>}
      </div>
    );
  }

  const { summary, customers, returned_count, total_matching } = data;

  return (
    <div
      ref={containerRef}
      className="bg-gradient-to-br from-[#0f0a1a] via-[#0a0a0a] to-[#0f0a1a] border border-purple-500/20 rounded-2xl p-6 space-y-5 transition-shadow duration-500"
      data-testid="customer-360-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Users2 size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Customer 360°</h3>
            <p className="text-gray-500 text-xs">
              Vista aggregata clienti per email · {summary.total_unique_customers} clienti unici
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-purple-400 transition-colors"
          data-testid="customer-360-refresh"
          title="Aggiorna"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="customer-360-kpis">
        <div className="bg-black/40 border border-gray-800 rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Users2 size={12} className="text-purple-400" />
            <p className="text-gray-400 text-[10px] uppercase tracking-wider">Clienti Unici</p>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_unique_customers}</p>
        </div>
        <div className="bg-black/40 border border-gray-800 rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Car size={12} className="text-purple-400" />
            <p className="text-gray-400 text-[10px] uppercase tracking-wider">Ricorrenti (≥2 veicoli)</p>
          </div>
          <p className="text-2xl font-bold text-purple-400">
            {summary.recurring_customers}
            <span className="text-gray-600 text-sm font-normal ml-1">({summary.recurrence_rate_pct}%)</span>
          </p>
        </div>
        <div className="bg-black/40 border border-gray-800 rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Crown size={12} className="text-lojack-clause-400" />
            <p className="text-gray-400 text-[10px] uppercase tracking-wider">Top Fedeli (≥4)</p>
          </div>
          <p className="text-2xl font-bold text-lojack-clause-400">{summary.top_loyal_customers}</p>
        </div>
        <div className="bg-black/40 border border-gray-800 rounded-xl p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Building2 size={12} className="text-blue-400" />
            <p className="text-gray-400 text-[10px] uppercase tracking-wider">Multi-Dealer</p>
          </div>
          <p className="text-2xl font-bold text-blue-400">{summary.multi_dealer_customers}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {countryFilter && (
          <span
            className="px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-300 border border-purple-500/40 flex items-center gap-1.5 font-medium"
            data-testid="c360-country-active-filter"
          >
            <MapPin size={12} />
            Paese: <span className="font-mono font-bold">{countryFilter}</span>
            <button
              onClick={() => setCountryFilter(null)}
              className="ml-1 text-purple-300 hover:text-white transition-colors"
              title="Rimuovi filtro paese"
              data-testid="c360-clear-country-filter"
            >
              <X size={11} />
            </button>
          </span>
        )}
        <span className="text-gray-500">Mostra:</span>
        {[
          { v: 1, label: "Tutti" },
          { v: 2, label: "Ricorrenti (≥2)" },
          { v: 4, label: "Top fedeli (≥4)" },
        ].map(opt => (
          <button
            key={opt.v}
            onClick={() => { setMinVehicles(opt.v); setExpandedEmail(null); }}
            className={`px-3 py-1.5 rounded-lg transition-colors ${
              minVehicles === opt.v
                ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                : "bg-gray-800 text-gray-400 hover:text-purple-400 border border-transparent"
            }`}
            data-testid={`c360-filter-min-${opt.v}`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-gray-700">|</span>
        <span className="text-gray-500">Ordina:</span>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="bg-[#0a0a0a] border border-gray-800 rounded-md px-2 py-1.5 text-white"
          data-testid="c360-sort-select"
        >
          <option value="total_vehicles">Più veicoli</option>
          <option value="signed_contracts">Più contratti firmati</option>
          <option value="last_activity">Attività recente</option>
          <option value="customer_since">Cliente più vecchio</option>
        </select>
        {expandedEmail && (
          <button
            onClick={() => setExpandedEmail(null)}
            className="ml-auto text-gray-500 hover:text-white flex items-center gap-1"
            data-testid="c360-collapse-all"
          >
            <X size={12} /> Chiudi dettagli
          </button>
        )}
      </div>

      {/* Customer list */}
      {customers.length === 0 ? (
        <div className="bg-black/40 border border-gray-800 rounded-xl p-8 text-center" data-testid="c360-empty-list">
          <p className="text-gray-500 text-sm">Nessun cliente corrisponde ai filtri selezionati.</p>
        </div>
      ) : (
        <div className="bg-black/40 border border-gray-800 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase tracking-wider">
                <th className="py-2 px-2 w-8"></th>
                <th className="py-2 px-2 text-left font-medium">Cliente</th>
                <th className="py-2 px-2 text-center font-medium">Fedeltà</th>
                <th className="py-2 px-2 text-center font-medium">Veicoli</th>
                <th className="py-2 px-2 text-center font-medium">Firmati</th>
                <th className="py-2 px-2 text-center font-medium">Paese</th>
                <th className="py-2 px-2 text-right font-medium">Ultima attività</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c, i) => (
                <CustomerRow
                  key={c.email}
                  customer={c}
                  index={i}
                  expanded={expandedEmail === c.email}
                  onToggle={() => setExpandedEmail(expandedEmail === c.email ? null : c.email)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-gray-600 text-[10px] text-center">
        Mostrati {returned_count} di {total_matching} clienti · Aggiornato:{" "}
        {new Date(data.generated_at).toLocaleString("it-IT")}
      </p>
    </div>
  );
};

export default Customer360Widget;
